import { useCallback } from 'react';
import { useUserActionMutation } from './useUserActionMutation';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';

export enum DiagnosticUserActionType {
  STARTED = 'ActionTypeNode:12',
  RESUMED = 'ActionTypeNode:13',
  SUBMITTED = 'ActionTypeNode:14',
  ENDED_EARLY = 'ActionTypeNode:25',
}

const actionTypeDescriptions: Record<DiagnosticUserActionType, string> = {
  [DiagnosticUserActionType.STARTED]: 'Diagnostic Started',
  [DiagnosticUserActionType.RESUMED]: 'Diagnostic Resumed',
  [DiagnosticUserActionType.SUBMITTED]: 'Diagnostic Submitted',
  [DiagnosticUserActionType.ENDED_EARLY]: 'Diagnostic Ended Early By Student',
};

/** Extra context recorded in the user-action Description for the end-early events (analytics). */
export interface EndEarlyContext {
  question: number;
  theta: number | null;
  standardError?: number | null;
  gate?: 'early' | 'late' | 'universal';
}

const formatEndEarlyDetails = ({ question, theta, standardError, gate }: EndEarlyContext): string =>
  [
    `q=${question}`,
    `theta=${theta ?? 'n/a'}`,
    standardError != null ? `se=${standardError}` : null,
    gate ? `gate=${gate}` : null,
  ]
    .filter(Boolean)
    .join('; ');

export const useDiagnosticUserAction = () => {
  const userRedux = useAppSelector(selectUser);
  const [commitMutation] = useUserActionMutation();

  const logDiagnosticUserAction = useCallback(
    ({
      actionType,
      diagnosticSessionId,
      details,
    }: {
      actionType: DiagnosticUserActionType;
      diagnosticSessionId: string;
      details?: string;
    }) => {
      if (!userRedux?.id) return;
      const base = actionTypeDescriptions[actionType] ?? 'Diagnostic Action';
      const description = details ? `${base} — ${details}` : base;
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userRedux?.id ?? ''}`,
              actionTypeId: `${actionType}`,
              timeStamp: new Date().toISOString(),
              description: `${description}`,
              diagnosticSessionId: diagnosticSessionId ?? '',
            },
          },
        },
      });
    },
    [commitMutation, userRedux?.id],
  );

  return {
    logDiagnosticUserAction,
    logStarted: (diagnosticSessionId: string) =>
      logDiagnosticUserAction({
        actionType: DiagnosticUserActionType.STARTED,
        diagnosticSessionId,
      }),
    logResumed: (diagnosticSessionId: string) =>
      logDiagnosticUserAction({
        actionType: DiagnosticUserActionType.RESUMED,
        diagnosticSessionId,
      }),
    logSubmitted: (diagnosticSessionId: string) =>
      logDiagnosticUserAction({
        actionType: DiagnosticUserActionType.SUBMITTED,
        diagnosticSessionId,
      }),
    logEndedEarly: (diagnosticSessionId: string, context: EndEarlyContext) =>
      logDiagnosticUserAction({
        actionType: DiagnosticUserActionType.ENDED_EARLY,
        diagnosticSessionId,
        details: formatEndEarlyDetails(context),
      }),
  };
};
